import { AccessLevel } from "@/api/williams/users";
import { faMagnifyingGlass, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { menus } from "../lookups/menus";
import { NewProjectDialog, SelectProjectDialog } from "../../projects/dialogs";
import { ScopeOfWork, ScopeOfWorkStatus } from "@/api/williams/scopesOfWork";
import { useContractorLookup } from "../../contractors/hooks";
import { useFormContext } from "react-hook-form";
import { useModalDialog } from "@/hooks";
import { usePermissions, useWeldingSpecialists } from "../../users/hooks";
import { useRegionsLookup, useScopeOfWorkList } from "../hooks";

export function SOWForm({ data }: { data: Readonly<ScopeOfWork> }) {
  const form = useFormContext<ScopeOfWork>();
  const modal = useModalDialog();
  const templates = useScopeOfWorkList({ templates: true });
  const specialists = useWeldingSpecialists();
  const contractors = useContractorLookup();
  const regionsLookup = useRegionsLookup();
  const permissions = usePermissions();

  const errors = form.formState.errors;
  const regions = form.watch("regions") ?? [];
  const isLocked = data.locked || permissions.data?.accessLevel === AccessLevel.GeneralUser;

  const handleSelectProject = (createNew: boolean) => {
    const dialog = createNew ? NewProjectDialog : SelectProjectDialog;
    modal.show(dialog, {
      initialProject: data.project,
      onAccept: (project) => {
        form.setValue("project", project, { shouldDirty: true });
        form.clearErrors("project");
      },
    });
  };

  const handleSelectRegions = () => {
    regionsLookup.showDialog({
      selectedRegions: regions,
      onAccept: (values) => {
        form.setValue("regions", values, { shouldDirty: true });
        form.clearErrors("regions");
      },
    });
  };

  return (
    <form>
      <div className="row">
        {/* First Column */}
        <div className="col-lg-6 pe-md-3 pe-xl-5">
          {/* SOW Number */}
          <div>
            <label htmlFor="number" className="col-form-label pb-0">
              SOW Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.number && "border-danger"}`}
              readOnly={true}
              tabIndex={-1}
              disabled={isLocked}
              {...form.register("number")}
            />
            {errors.number && <span className="text-danger">{errors.number.message}</span>}
          </div>
          {/* SOW Description */}
          <div>
            <label htmlFor="description" className="col-form-label pb-0">
              SOW Description <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              maxLength={50}
              className={`form-control ${errors.description && "border-danger"}`}
              disabled={isLocked}
              autoComplete="off"
              {...form.register("description")}
            />
            {errors.description && <span className="text-danger">{errors.description.message}</span>}
          </div>
          {/* Project Number */}
          <div>
            <label htmlFor="projectNumber" className="col-form-label pb-0">
              Project Number <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <input
                type="text"
                className={`form-control ${errors.project && "border-danger"}`}
                readOnly
                tabIndex={-1}
                disabled={isLocked}
                {...form.register("project.number")}
              />
              {!isLocked && (
                <span className="input-group-text" role="button" onClick={() => handleSelectProject(false)}>
                  <FontAwesomeIcon size="sm" icon={faMagnifyingGlass} title="Search" />
                </span>
              )}
              {!isLocked && (
                <span className="input-group-text" role="button" onClick={() => handleSelectProject(true)}>
                  <FontAwesomeIcon size="sm" icon={faPlus} title="New Project" />
                </span>
              )}
            </div>
            {errors.project?.number && <span className="text-danger">{errors.project.number.message}</span>}
          </div>
          {/* Project Id */}
          <div>
            {/* Required for validation to work properly */}
            <input type="hidden" {...form.register("project.id")} />
          </div>
          {/* Project Name */}
          <div>
            <label htmlFor="projectName" className="col-form-label pb-0">
              Project Name
            </label>
            <input
              type="text"
              className="form-control"
              readOnly
              tabIndex={-1}
              disabled={isLocked}
              {...form.register("project.name")}
            />
          </div>
          {/* Project Description */}
          <div>
            <label htmlFor="projectDescription" className="col-form-label pb-0">
              Project Description
            </label>
            <input
              type="text"
              className="form-control"
              readOnly
              tabIndex={-1}
              disabled={isLocked}
              {...form.register("project.description")}
            />
          </div>
          {/* Report Date */}
          <div>
            <label htmlFor="reportDate" className="col-form-label pb-0">
              Report Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              min="1753-01-01"
              className={`form-control ${errors.reportDate && "border-danger"}`}
              disabled={isLocked}
              {...form.register("reportDate")}
            />
            {errors.reportDate && <span className="text-danger">{errors.reportDate.message}</span>}
          </div>
          {/* Regions */}
          <div>
            <label htmlFor="regions" className="col-form-label pb-0">
              Region(s) {data.status > ScopeOfWorkStatus.Partial && <span className="text-danger">*</span>}
            </label>
            <div className="input-group">
              <input
                type="text"
                className={`form-control ${errors.regions && "border-danger"}`}
                readOnly
                tabIndex={-1}
                disabled={isLocked}
                value={regions.map((region) => region.name).join(", ")}
              />
              {!isLocked && (
                <span className="input-group-text" role="button" onClick={handleSelectRegions}>
                  <FontAwesomeIcon size="sm" icon={faMagnifyingGlass} title="Search" />
                </span>
              )}
            </div>
            {errors.regions && <span className="text-danger">{errors.regions.message}</span>}
          </div>
          {/* Welding/NDE Specialist */}
          <div>
            <label htmlFor="weldingSpecialistId" className="col-form-label pb-0">
              Welding/NDE Specialist <span className="text-danger">*</span>
            </label>
            <select
              className={`form-select ${errors.weldingSpecialistId && "border-danger"}`}
              disabled={isLocked}
              {...form.register("weldingSpecialistId")}
            >
              <option />
              {specialists.data &&
                specialists.data.map((specialist) => (
                  <option key={specialist.id} value={specialist.id}>
                    {specialist.name ?? specialist.userName}
                  </option>
                ))}
            </select>
            {errors.weldingSpecialistId && <span className="text-danger">{errors.weldingSpecialistId.message}</span>}
          </div>
          {/* Owner Id */}
          <div>
            {/* Required for validation to work properly */}
            <input type="hidden" {...form.register("owner")} />
          </div>
          {/* SOW Owner */}
          <div>
            <label htmlFor="owner" className="col-form-label pb-0">
              SOW Owner <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={data.owner?.name ?? data.owner?.userName}
              readOnly
              tabIndex={-1}
              disabled={isLocked}
            />
          </div>
          {/* Common SOW */}
          <div>
            <label htmlFor="commonSOW" className="col-form-label pb-0">
              Common SOW
            </label>
            <select
              className={`form-select ${errors.commonScopeOfWorkId && "border-danger"}`}
              disabled={isLocked}
              {...form.register("commonScopeOfWorkId")}
            >
              <option />
              {templates.isSuccess &&
                templates.data.pages
                  .flatMap((x) => x.results)
                  .map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.number}
                    </option>
                  ))}
            </select>
            {errors.commonScopeOfWorkId && <span className="text-danger">{errors.commonScopeOfWorkId.message}</span>}
          </div>
        </div>

        {/* Second Column */}
        <div className="col-lg-6 ps-md-3 ps-xl-5">
          {/* Contractor */}
          <div>
            <label htmlFor="contractor" className="col-form-label pb-0">
              Contractor
            </label>
            <select
              className={`form-select ${errors.contractorId && "border-danger"}`}
              disabled={isLocked}
              {...form.register("contractorId")}
            >
              <option />
              {contractors.data?.results &&
                contractors.data.results.map((contractor) => (
                  <option key={contractor.id} value={contractor.id}>
                    {contractor.name}
                  </option>
                ))}
            </select>
            {errors.contractorId && <span className="text-danger">{errors.contractorId.message}</span>}
          </div>
          {/* Construction Code */}
          <div>
            <label htmlFor="constructionCode" className="col-form-label pb-0">
              Construction Code {data.status > ScopeOfWorkStatus.Partial && <span className="text-danger">*</span>}
            </label>
            <select
              className={`form-select ${errors.constructionCode && "border-danger"}`}
              disabled={isLocked}
              {...form.register("constructionCode")}
            >
              <option />
              {menus.constructionCode.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
            {errors.constructionCode && <span className="text-danger">{errors.constructionCode.message}</span>}
          </div>
          {/* WPS Code */}
          <div>
            <label htmlFor="wpsCode" className="col-form-label pb-0">
              WPS Code {data.status > ScopeOfWorkStatus.Partial && <span className="text-danger">*</span>}
            </label>
            <select
              className={`form-select ${errors.wpsCode && "border-danger"}`}
              disabled={isLocked}
              {...form.register("wpsCode")}
            >
              <option />
              {menus.wpsCode(data).map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
            {errors.wpsCode && <span className="text-danger">{errors.wpsCode.message}</span>}
          </div>
          {/* Service */}
          <div>
            <label htmlFor="service" className="col-form-label pb-0">
              Service {data.status > ScopeOfWorkStatus.Partial && <span className="text-danger">*</span>}
            </label>
            <select
              className={`form-select ${errors.service && "border-danger"}`}
              disabled={isLocked}
              {...form.register("service")}
            >
              <option />
              <option>Normal</option>
              <option>Sour</option>
            </select>
            {errors.service && <span className="text-danger">{errors.service.message}</span>}
          </div>
          {/* Materials */}
          <div>
            <label htmlFor="material" className="col-form-label pb-0">
              Materials {data.status > ScopeOfWorkStatus.Partial && <span className="text-danger">*</span>}
            </label>
            <select
              className={`form-select ${errors.materials && "border-danger"}`}
              disabled={isLocked}
              {...form.register("materials")}
            >
              <option />
              {menus.material.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
            {errors.materials && <span className="text-danger">{errors.materials.message}</span>}
          </div>
          {/* Thickness Groove Weld */}
          <div>
            <label htmlFor="thicknessGrooveWeld" className="col-form-label pb-0">
              Thickest Groove Weld {data.status > ScopeOfWorkStatus.Partial && <span className="text-danger">*</span>}
            </label>
            <select
              className={`form-select ${errors.thickestGrooveWeld && "border-danger"}`}
              disabled={isLocked}
              {...form.register("thickestGrooveWeld")}
            >
              <option />
              {menus.thickestGroove.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
            {errors.thickestGrooveWeld && <span className="text-danger">{errors.thickestGrooveWeld.message}</span>}
          </div>
          {/* NPS of Smallest Groove Weld */}
          <div>
            <label htmlFor="smallestGrooveWeld" className="col-form-label pb-0">
              NPS of Smallest Groove Weld{" "}
              {data.status > ScopeOfWorkStatus.Partial && <span className="text-danger">*</span>}
            </label>
            <select
              className={`form-select ${errors.smallestGrooveWeld && "border-danger"}`}
              disabled={isLocked}
              {...form.register("smallestGrooveWeld")}
            >
              <option />
              {menus.npsGroove.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
            {errors.smallestGrooveWeld && <span className="text-danger">{errors.smallestGrooveWeld.message}</span>}
          </div>
          {/* Continuity Type */}
          <div>
            <label htmlFor="continuityType" className="col-form-label pb-0">
              Continuity Type {data.status > ScopeOfWorkStatus.Partial && <span className="text-danger">*</span>}
            </label>
            <select
              className={`form-select ${errors.continuityType && "border-danger"}`}
              disabled={isLocked}
              {...form.register("continuityType")}
            >
              <option />
              {menus.continuity(data).map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
            {errors.continuityType && <span className="text-danger">{errors.continuityType.message}</span>}
          </div>
          <div className="mt-3">
            {/* In-service Welding */}
            <div className="form-check form-switch ">
              <input
                id="inServiceWelding"
                className="form-check-input"
                type="checkbox"
                disabled={isLocked}
                {...form.register("inServiceWelding")}
              />
              <label className="form-check-label" htmlFor="inServiceWelding">
                In-service Welding
              </label>
            </div>
            {/* Allow Qualifications by NDE */}
            <div className="form-check form-switch">
              <input
                id="allowQualificationsByNde"
                className="form-check-input"
                type="checkbox"
                disabled={isLocked}
                {...form.register("allowQualificationsByNde")}
              />
              <label className="form-check-label" htmlFor="allowQualificationsByNde">
                Allow Qualifications by NDE
              </label>
            </div>
            {/* Apply Repair Welder Qualification Requirements */}
            <div className="form-check form-switch">
              <input
                id="applyRepairWelderQualReqs"
                className="form-check-input"
                type="checkbox"
                disabled={isLocked}
                {...form.register("applyRepairWelderQualReqs")}
              />
              <label className="form-check-label" htmlFor="applyRepairWelderQualReqs">
                Apply Repair Welder Qualification Requirements
              </label>
            </div>
            {/* New York Project */}
            <div className="form-check form-switch">
              <input
                id="newYorkProject"
                className="form-check-input"
                type="checkbox"
                disabled={isLocked}
                {...form.register("newYorkProject")}
              />
              <label className="form-check-label" htmlFor="newYorkProject">
                New York Project
              </label>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
